import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownIcon, ArrowRightIcon, Building2Icon, CheckCircle2Icon, LayersIcon, SparklesIcon, UserIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { trackEvent as track } from "../../utils/analytics";
import { promiseCopy as c, Bi } from "../../data/brossardCase";
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const STEP_ICONS: Record<(typeof c.steps)[number]['key'], BoxIcon> = {
  identity: UserIcon,
  services: LayersIcon,
  atna: SparklesIcon,
  actions: CheckCircle2Icon
};
export function BrossardPromise() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative bg-white border-t border-[#EEF0F4] px-6 pt-14 lg:pt-16 pb-16 lg:pb-20" aria-labelledby="brossard-promise-title" data-id="element-1292" data-mp="8hg41losbqye">
      <div className="max-w-[1320px] mx-auto" data-id="element-1293" data-mp="1o8scbncv5tji">
        <motion.div {...reveal} className="grid grid-cols-1 xl:grid-cols-[0.95fr_0.85fr_1.9fr] gap-8 xl:gap-10 items-center" data-id="element-1294" data-mp="1obmvlbk558bc">
          <div data-id="element-1295" data-mp="1y8j7lr990ckn">
            <p className="font-sans text-[11.5px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-1296" data-mp="1y89cxqd9dh8m">{pick(c.eyebrow)}</p>
            <h2 id="brossard-promise-title" className="mt-4 font-serif text-[1.9rem] sm:text-[2.2rem] leading-[1.06] tracking-tight" data-id="element-1297" data-mp="10lslv2v4bzvb">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-1298" data-mp="1ahvjpwixtccg">{pick(l.text)}</span>)}
            </h2>
          </div>
          <p className="font-sans text-[14px] text-[#4A5268] leading-relaxed max-w-[520px]" data-id="element-1299" data-mp="1fb8aac76ft7u">{pick(c.body)}</p>

          <ol className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-2 items-stretch" data-id="element-1300" data-mp="hrx0wivc6bg8">
            {c.steps.map((s, i) => {
            const Icon = STEP_ICONS[s.key];
            return <React.Fragment key={s.key} data-id="element-1301" data-mp="epsb3s3l990v" data-mpi={i}>
                  <li className="rounded-xl border border-[#ECEBF5] bg-white px-3 py-4 text-center flex flex-col items-center" data-id="element-1302" data-mp="9s0kr2ilduys">
                    <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-1303" data-mp="oh6eiaynusza">
                      <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1304" data-mp="jky5lfb23w0k" />
                    </span>
                    <h3 className="mt-3 font-sans text-[11px] font-bold tracking-[0.1em] uppercase text-[#121A3A] leading-snug" data-id="element-1305" data-mp="3mzttsoqnyx2e">
                      {s.title.map(t => <span key={t.fr} className="block" data-id="element-1306" data-mp="5t1hrngf889z">{pick(t)}</span>)}
                    </h3>
                    <p className="mt-2.5 font-sans text-[12px] text-[#6B7288] leading-snug" data-id="element-1307" data-mp="qslah4cao34">{pick(s.text)}</p>
                  </li>
                  {i < c.steps.length - 1 && <li className="flex items-center justify-center" aria-hidden="true" data-id="element-1308" data-mp="1c823l0bqfa2u">
                      <ArrowRightIcon className="hidden md:block w-4 h-4 text-[#6D66EE]" strokeWidth={1.6} data-id="element-1309" data-mp="1jrbcytk8n8rw" />
                      <ArrowDownIcon className="md:hidden w-4 h-4 text-[#6D66EE]" strokeWidth={1.6} data-id="element-1310" data-mp="1oqnynazdi9sw" />
                    </li>}
                </React.Fragment>;
          })}
          </ol>
        </motion.div>

        <motion.div {...reveal} className="mt-10 rounded-2xl border border-[#E7E6F5] bg-[#F7F7FD] px-6 md:px-10 py-6 flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-1311" data-mp="1erf9qwjd7lde">
          <Building2Icon className="w-10 h-10 text-[#4F46E5] shrink-0" strokeWidth={1.4} aria-hidden="true" data-id="element-1312" data-mp="22jvsl9axfhdr" />
          <p className="flex-1 font-serif text-[1.3rem] md:text-[1.5rem] leading-snug text-[#121A3A]" data-id="element-1313" data-mp="1tp7lddlsjpmb">
            {pick(c.cta.line1)}
            <br className="hidden sm:block" data-id="element-1314" data-mp="1xprjoki3oza1" /> {pick(c.cta.line2)}
            <span className="text-[#4F46E5] whitespace-nowrap" data-id="element-1315" data-mp="2lgf2i0tk19bd">{pick(c.cta.accent)}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1316" data-mp="2q6vjfuwl3d2h">
            <Link to="/request-demo" onClick={() => track('brossard_demo_click')} className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-[#4F46E5] px-6 py-3 font-sans text-[14.5px] font-medium text-white whitespace-nowrap hover:bg-[#4338CA] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-1317" data-mp="2q8f19pxw1w6d">
              {pick(c.cta.primary)}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1318" data-mp="1l9aadep12zev" />
            </Link>
            <Link to="/platform" className="inline-flex items-center justify-center gap-2.5 rounded-lg border border-[#DCDDE6] bg-white px-6 py-3 font-sans text-[14.5px] font-medium text-[#121A3A] whitespace-nowrap hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors duration-150" data-id="element-1319" data-mp="1lb4ly2eetf6a">
              {pick(c.cta.secondary)}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1320" data-mp="240b9c2q5euyf" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}